import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Collapse, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeBranch, faFolder, faFolderOpen, faMinus, faPlus, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { GitAdminTreeProject, GitAdminTreeResponse } from '../../interfaces';

type TreeNode = {
  label: string;
  path: string;
  children: TreeNode[];
  projects: GitAdminTreeProject[];
};

interface GitAdminHierarchyProps {
  tree: GitAdminTreeResponse | null;
  loading?: boolean;
  onExpand?: () => void;
}

const isSuccessStatus = (status?: string | null): boolean =>
  (status ?? '').toLowerCase() === 'success';

const renderStatusDot = (status?: string | null) => (
  <span
    className={`gitadmin-status-dot ${isSuccessStatus(status) ? 'gitadmin-status-dot-success' : 'gitadmin-status-dot-failed'}`}
    title={status ?? 'KO'}
    aria-label={status ?? 'KO'}
  />
);

const renderCoveragePie = (coverage: number | null) => {
  if (coverage === null) {
    return <span>N/A</span>;
  }

  const normalizedCoverage = Math.max(0, Math.min(100, coverage));
  const isLowCoverage = normalizedCoverage < 80;
  const circumference = 100;

  return (
    <svg className="gitadmin-coverage-pie" viewBox="0 0 36 36" role="img" aria-label={`${normalizedCoverage.toFixed(1)}%`}>
      <title>{normalizedCoverage.toFixed(1)}%</title>
      <circle className="gitadmin-coverage-track" cx="18" cy="18" r="15.915" pathLength={circumference} />
      <circle
        className={`gitadmin-coverage-value ${isLowCoverage ? 'gitadmin-coverage-value-low' : 'gitadmin-coverage-value-ok'}`}
        cx="18"
        cy="18"
        r="15.915"
        pathLength={circumference}
        strokeDasharray={`${normalizedCoverage} ${circumference}`}
      />
    </svg>
  );
};

const renderLinkIcon = (url?: string | null, label?: string, type: 'issue' | 'merge-request' = 'merge-request') => {
  if (!url) {
    return <span className="text-muted">-</span>;
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" aria-label={label ?? 'Open link'} title={label ?? 'Open link'}>
      <FontAwesomeIcon icon={type === 'issue' ? faTriangleExclamation : faCodeBranch} />
    </a>
  );
};

const buildTree = (projects: GitAdminTreeProject[]): TreeNode[] => {
  const nodeMap = new Map<string, TreeNode>();

  const ensureNode = (path: string, label: string) => {
    const existing = nodeMap.get(path);
    if (existing) {
      return existing;
    }

    const created: TreeNode = { label, path, children: [], projects: [] };
    nodeMap.set(path, created);
    return created;
  };

  projects.forEach(project => {
    const namespace = project.namespace || 'hobud';
    const segments = namespace.split('/').filter(Boolean);
    let currentPath = '';

    segments.forEach(segment => {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      ensureNode(currentPath, segment);
    });

    const projectNode = ensureNode(namespace, segments[segments.length - 1] || namespace);
    projectNode.projects.push(project);
  });

  const roots: TreeNode[] = [];
  nodeMap.forEach(node => {
    const parentPath = node.path.includes('/') ? node.path.slice(0, node.path.lastIndexOf('/')) : '';
    if (parentPath && nodeMap.has(parentPath)) {
      nodeMap.get(parentPath)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNode = (node: TreeNode): TreeNode => ({
    ...node,
    children: node.children.sort((left, right) => left.label.localeCompare(right.label)).map(sortNode),
    projects: node.projects.sort((left, right) => left.name.localeCompare(right.name))
  });

  return roots.sort((left, right) => left.label.localeCompare(right.label)).map(sortNode);
};

const GitAdminHierarchy: React.FC<GitAdminHierarchyProps> = ({ tree, loading = false, onExpand }) => {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedProjects, setExpandedProjects] = useState<Record<number, boolean>>({});
  const [showTree, setShowTree] = useState(false);

  const treeNodes = useMemo(() => buildTree(tree?.projects ?? []), [tree]);

  useEffect(() => {
    if (treeNodes.length === 0) {
      return;
    }

    setExpandedGroups(prev => {
      const next = { ...prev };

      const expandNode = (node: TreeNode) => {
        if (next[node.path] === undefined) {
          next[node.path] = true;
        }

        node.children.forEach(expandNode);
      };

      treeNodes.forEach(expandNode);
      return next;
    });
  }, [treeNodes]);

  const renderProject = (project: GitAdminTreeProject) => {
    const isExpanded = expandedProjects[project.gitlabProjectId];

    return (
      <div key={project.gitlabProjectId} className="mb-2">
        <div className="d-flex align-items-center gap-2 py-1">
          <Button
            variant="link"
            className="p-0 text-decoration-none text-start d-inline-flex align-items-center gap-2"
            onClick={() => setExpandedProjects(prev => ({ ...prev, [project.gitlabProjectId]: !prev[project.gitlabProjectId] }))}
          >
            <span className="d-inline-flex align-items-center justify-content-center gitadmin-toggle-icon-slot">
              <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} />
            </span>
            <FontAwesomeIcon icon={isExpanded ? faFolderOpen : faFolder} />
            <span>{project.name}</span>
          </Button>
          <Badge bg="info">{project.defaultBranch || 'main'}</Badge>
          <Badge bg={project.isActive ? 'success' : 'secondary'}>{project.isActive ? t('yes') : t('no')}</Badge>
        </div>
        <Collapse in={isExpanded}>
          <div className="ms-4 ps-3 border-start border-2 border-secondary-subtle">
            <Card className="border-0 shadow-none">
              <Card.Body className="px-0 py-2">
              <Table striped hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>{t('gitAdmin.branch')}</th>
                    <th>C%</th>
                    <th>Pipe</th>
                    <th>{t('gitAdmin.mergeRequest')}</th>
                    <th>Links</th>
                    <th>{t('gitAdmin.issueOwner')}</th>
                    <th>{t('gitAdmin.mergeRequestOwner')}</th>
                  </tr>
                </thead>
                <tbody>
                  {project.branches.map(branch => (
                    <tr key={branch.name}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faCodeBranch} className="text-muted" />
                          <span>{branch.name}</span>
                          {branch.isDefault && <Badge bg="warning" text="dark">{t('gitAdmin.defaultBranch')}</Badge>}
                        </div>
                      </td>
                      <td>
                        {renderCoveragePie(branch.coverage)}
                      </td>
                      <td>
                        {renderStatusDot(branch.pipelineStatus)}
                      </td>
                      <td>
                        {branch.mergeRequestTitle ? (
                          <div>
                            <div>{branch.mergeRequestTitle}</div>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {renderLinkIcon(branch.issueUrl, 'Open issue', 'issue')}
                          {renderLinkIcon(branch.mergeRequestUrl, 'Open merge request', 'merge-request')}
                        </div>
                      </td>
                      <td>
                        <div>{branch.issueTitle ?? 'N/A'}</div>
                        <div className="small text-muted">{branch.issueOwner ?? 'N/A'}</div>
                      </td>
                      <td>{branch.mergeRequestOwner ?? 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </Card.Body>
            </Card>
          </div>
        </Collapse>
      </div>
    );
  };

  const renderNode = (node: TreeNode) => {
    const isExpanded = expandedGroups[node.path] ?? false;

    return (
      <div key={node.path} className="mb-2">
        <div className="d-flex align-items-center gap-2 py-1">
          <Button
            variant="link"
            className="p-0 text-decoration-none text-start d-inline-flex align-items-center gap-2"
            onClick={() => setExpandedGroups(prev => ({ ...prev, [node.path]: !isExpanded }))}
          >
            <span className="d-inline-flex align-items-center justify-content-center gitadmin-toggle-icon-slot">
              <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} />
            </span>
            <FontAwesomeIcon icon={isExpanded ? faFolderOpen : faFolder} />
            <span>{node.label}</span>
          </Button>
        </div>
        <Collapse in={isExpanded}>
          <div className="ms-4 ps-3 border-start border-2 border-secondary-subtle">
            {node.children.map(child => renderNode(child))}
            {node.projects.map(project => renderProject(project))}
          </div>
        </Collapse>
      </div>
    );
  };

  if (loading && treeNodes.length === 0) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Body className="d-flex justify-content-center">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <span>
            {t('gitAdmin.treeTitle')} <Badge bg="secondary">{tree?.projects.length ?? 0}</Badge>
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setShowTree(value => {
              const next = !value;
              if (next) {
                onExpand?.();
              }
              return next;
            })}
          >
            {showTree ? 'Masquer' : 'Afficher'}
          </Button>
        </div>
      </Card.Header>
      <Collapse in={showTree}>
        <div>
          <Card.Body>
            {treeNodes.map(node => renderNode(node))}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default GitAdminHierarchy;
